import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { WeatherInformationApiService } from '../../services/weather-information/weather-information-api.service';
import {
  CreateWeatherInformation,
  DeleteWeatherInformation,
  LoadAllWeatherInformation,
  LoadWeatherInformation,
  LoadWeatherInformationById,
  UpdateWeatherInformation,
  ClearCurrentWeatherInformation,
} from './non-conformance-weather-information.actions';

export interface WeatherInformationStateModel {
  weatherInformations: IdName[];
  allWeatherInformations: IdName[];
  weatherInformationTotalPages: number;
  currentWeatherInformation: IdName | undefined;
}

const NC_WEATHER_INFORMATION_STATE_TOKEN = new StateToken<WeatherInformationStateModel>(
  'ncWeatherInformations'
);

@State<WeatherInformationStateModel>({
  name: NC_WEATHER_INFORMATION_STATE_TOKEN,
  defaults: {
    weatherInformations: [],
    allWeatherInformations: [],
    weatherInformationTotalPages: 0,
    currentWeatherInformation: undefined,
  },
})
@Injectable()
export class NonConformanceWeatherInformationState {
  constructor(private service: WeatherInformationApiService) {}

  @Action(LoadWeatherInformation)
  loadWeatherInformations(
    { getState, setState }: StateContext<WeatherInformationStateModel>,
    { filter }: LoadWeatherInformation
  ) {
    return this.service.loadWeatherInformation(filter).pipe(
      tap((response: ApiResponse<IdName[]>) => {
        const state = getState();
        setState({
          ...state,
          weatherInformations: response.data,
          weatherInformationTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllWeatherInformation, { cancelUncompleted: true })
  loadAllWeatherInformations(ctx: StateContext<WeatherInformationStateModel>) {
    return this.service.loadAllWeatherInformation().pipe(
      tap((allWeatherInformations: IdName[]) => {
        ctx.setState(patch({ allWeatherInformations }));
      })
    );
  }

  @Action(LoadWeatherInformationById, { cancelUncompleted: true })
  loadWeatherInformationById(
    { getState, setState }: StateContext<WeatherInformationStateModel>,
    { id }: LoadWeatherInformationById
  ) {
    const { weatherInformations } = getState();
    const exist = weatherInformations.some((i) => i.id === id);
    return this.service.loadWeatherInformationById(id).pipe(
      tap((weatherInformation: IdName) => {
        setState(
          patch({
            weatherInformations: exist
              ? updateItem<IdName>((toUpdate) => toUpdate?.id === id, weatherInformation)
              : insertItem<IdName>(weatherInformation),
            currentWeatherInformation: weatherInformation,
          })
        );
      })
    );
  }

  @Action(CreateWeatherInformation)
  @OnSuccess({
    url: AdminNonConformanceRoutes.WeatherInformation,
    message: 'weatherInformationAdded',
  })
  createWeatherInformation(
    { setState }: StateContext<WeatherInformationStateModel>,
    { weatherInformation }: CreateWeatherInformation
  ) {
    return this.service.createWeatherInformation(weatherInformation).pipe(
      tap((response: IdName) => {
        setState(
          patch({
            weatherInformations: insertItem<IdName>(response),
          })
        );
      })
    );
  }

  @Action(UpdateWeatherInformation)
  @OnSuccess({
    url: AdminNonConformanceRoutes.WeatherInformation,
    message: 'weatherInformationUpdated',
  })
  updateWeatherInformation(
    { setState }: StateContext<WeatherInformationStateModel>,
    { weatherInformation }: UpdateWeatherInformation
  ) {
    return this.service.updateWeatherInformation(weatherInformation).pipe(
      tap(() => {
        setState(
          patch({
            weatherInformations: updateItem<IdName>(
              (toUpdate) => toUpdate?.id === weatherInformation.id,
              weatherInformation
            ),
          })
        );
      })
    );
  }

  @Action(DeleteWeatherInformation, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminNonConformanceRoutes.WeatherInformation,
    message: 'weatherInformationDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteWeatherInformation(
    ctx: StateContext<WeatherInformationStateModel>,
    { id }: DeleteWeatherInformation
  ) {
    return this.service.deleteWeatherInformation(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            weatherInformations: removeItem<IdName>(
              (wtToDelete: IdName | undefined) => wtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentWeatherInformation)
  clearCurrentWeatherInformation(ctx: StateContext<WeatherInformationStateModel>) {
    ctx.setState(patch({ currentWeatherInformation: undefined }));
  }

  @Selector()
  static getWeatherInformationsTotalPages(state: WeatherInformationStateModel) {
    return state.weatherInformationTotalPages;
  }

  @Selector()
  static weatherInformations(state: WeatherInformationStateModel) {
    return state.weatherInformations;
  }

  @Selector()
  static allWeatherInformations(state: WeatherInformationStateModel): IdName[] {
    return state.allWeatherInformations;
  }

  @Selector()
  static getWeatherInformationById(state: WeatherInformationStateModel) {
    return (id: string) => {
      return state.weatherInformations.find(
        (WeatherInformation: IdName) => WeatherInformation.id === id
      );
    };
  }

  @Selector()
  static getCurrentWeatherInformation(state: WeatherInformationStateModel) {
    return state.currentWeatherInformation;
  }
}
